import { memo, useState } from 'react';
import { Check, Copy, ThumbsDown, ThumbsUp } from 'lucide-react';
import { Button } from '../components/atoms/Button';
import { apiClient } from '../api/client';
import { markdownToRichTextWithoutLinks, stripMarkdownLinks, cn } from '../lib/utils';

interface Props {
  messageId: string;
  text: string;
  /** Initial feedback state loaded from history (null when never rated). */
  initialFeedback?: 'Liked' | 'Disliked' | null;
}

/**
 * Like / Dislike / Copy action row shown under every AI answer.
 *
 * Mirrors frontend-prod's MessageCard.tsx:668-719 — same endpoint, same
 * payload, same clipboard-dual-format (HTML + plain text) so pasting into
 * Word preserves formatting while Slack/email get readable plain text.
 *
 * Memoised — re-renders only when message id/text or initial feedback
 * changes, which is rare once the message has streamed in.
 */
function MessageActionsImpl({ messageId, text, initialFeedback = null }: Props): JSX.Element {
  const [feedback, setFeedback] = useState<'Liked' | 'Disliked' | null>(initialFeedback);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const submitFeedback = async (type: 'Liked' | 'Disliked') => {
    if (isSubmitting) return;
    // Optimistic toggle: click same side again to clear it, click other side to switch
    const nextLocal = feedback === type ? null : type;
    try {
      setIsSubmitting(true);
      await apiClient.put('/api/v1/chat/messages/feedback', {
        message_id: messageId,
        feedback: nextLocal ?? '',
      });
      setFeedback(nextLocal);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[MessageActions] feedback submit failed', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = async () => {
    const html = markdownToRichTextWithoutLinks(text);
    const plain = stripMarkdownLinks(text);

    try {
      // Dual-format write so Word + rich-text targets get HTML, and
      // plain-text targets (terminals, Slack plain paste) get stripped text.
      const item = new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plain], { type: 'text/plain' }),
      });
      await navigator.clipboard.write([item]);
    } catch {
      // WKWebView / older WebView2 may not support ClipboardItem — fall back.
      try {
        await navigator.clipboard.writeText(plain);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[MessageActions] clipboard write failed', err);
        return;
      }
    }
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <div className="mt-3 pt-2 border-t border-border/50 flex items-center gap-1">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => submitFeedback('Liked')}
        disabled={isSubmitting}
        className={cn(
          'h-7 px-2 text-[11px] transition-colors',
          feedback === 'Liked'
            ? 'text-green-600 bg-green-50 hover:bg-green-100'
            : 'text-muted-foreground hover:text-green-600 hover:bg-green-50',
        )}
        title="Good response"
      >
        <ThumbsUp className="w-3 h-3 mr-1" />
        Like
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => submitFeedback('Disliked')}
        disabled={isSubmitting}
        className={cn(
          'h-7 px-2 text-[11px] transition-colors',
          feedback === 'Disliked'
            ? 'text-red-600 bg-red-50 hover:bg-red-100'
            : 'text-muted-foreground hover:text-red-600 hover:bg-red-50',
        )}
        title="Bad response"
      >
        <ThumbsDown className="w-3 h-3 mr-1" />
        Dislike
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={copyToClipboard}
        className="h-7 px-2 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
        title="Copy answer"
      >
        {isCopied ? (
          <>
            <Check className="w-3 h-3 mr-1 text-green-600" />
            Copied
          </>
        ) : (
          <>
            <Copy className="w-3 h-3 mr-1" />
            Copy
          </>
        )}
      </Button>
    </div>
  );
}

export const MessageActions = memo(MessageActionsImpl);
